import { getLists } from '@/services/listsService';
import { getBusinesses } from '@/services/dashboardService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { checkBusinessesPartOfSequence } from '@/services/sequencesService';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BUSINESS_PER_SCROLL } from '../config';
import { useSequencesContext } from '../SequencesContext';

export type BusinessListType = {
	businessIds: string[];
	createdAt: string;
	deleted: boolean;
	id: string;
	location: {
		coordinates: number[];
		type: string;
	};
	name: string;
	smartList: boolean;
	updatedAt: string;
	userId: string;
};

export function useBusinessList() {
	const {
		selectedLists,
		businessesInLists,
		setBusinessesInLists,
		selectedSequence,
		sequences,
		businessLists,
		setBusinessLists,
		setSelectedBusinesses,
		hasMoreBusinessesToFetch,
		setHasMoreBusinessesToFetch,
		businessesToRemove,
		setBusinessesAlreadyInSequence,
		allBusinessesSelect,
		setIsBusinessesLoading,
		setSelectedLists,
		businessesScrollPage
	} = useSequencesContext();

	const history = useHistory();

	const [query] = useSelector(
		(state: RootState) => [state.dashboard.get('query').toJS()],
		shallowEqual
	);

	const [businessesFirstLoading, setBusinessesFirstLoading] =
		useState<boolean>(true);

	const loadingBusiness = useRef(false);

	async function fetchBusinessesLists() {
		const searchParams = new URLSearchParams(window.location.search);
		const listIds = searchParams.get('listId')?.split(',');

		const res = await getLists();

		if (res.data) {
			const lists = res.data.data;
			setBusinessLists(lists);

			if (listIds) {
				setSelectedLists(
					lists.filter((list: BusinessListType) =>
						listIds.includes(list.id)
					)
				);

				// Remove listId from the URL
				searchParams.delete('listId');
				history.replace({ search: searchParams.toString() });
			}
		}
	}

	function getBusinessesInSequence() {
		const currentSequence = sequences?.find(
			sequence => sequence.id === selectedSequence?.id
		);
		const businessesInSequence = currentSequence?.businesses || [];
		return businessesInSequence;
	}

	function getListsFromSelectedBusinesses() {
		const businessesInSequence = getBusinessesInSequence();

		const listsFromBusinesses = businessesInSequence.map(
			business => business.listOriginId
		);

		return selectedLists.length > 0
			? selectedLists
			: businessLists.filter(list =>
					listsFromBusinesses.includes(list.id)
				);
	}

	async function fetchBusinessesInLists(
		{ page }: { page: number } = { page: 1 }
	) {
		if (!hasMoreBusinessesToFetch) return;
		if (selectedLists.length === 0) return;
		loadingBusiness.current = true;

		setIsBusinessesLoading(true);
		try {
			const res = await getBusinesses({
				...query,
				sortField: 'name',
				sort: query.sort === 1 ? { name: '1' } : query.sort,
				limit: BUSINESS_PER_SCROLL,
				skip: (page - 1) * BUSINESS_PER_SCROLL,
				listIds: selectedLists.map(list => list.id),
				listId: undefined // This can be filled by the query so we need to make sure its undefined
			});

			if (res.data) {
				let businessesFetched = res.data.data.result;
				const businessesCount = res.data.data.count;

				// **Filter businesses to ensure they have at least one email**
				businessesFetched = businessesFetched.filter(
					b =>
						b.email ||
						b.facebook?.email ||
						b.web?.email ||
						b.userAddedData?.some(a => a.type === 'email')
				);

				const newBusinessesList = businessesFetched.map(business => ({
					...business,
					listId: selectedLists
						.flat()
						.find(list => list.businessIds.includes(business._id))
						?.id
				}));

				const businessesToAdd =
					page === 1
						? newBusinessesList
						: businessesInLists.concat(newBusinessesList);

				if (selectedSequence?.id && businessesFetched.length > 0) {
					const businessIds = businessesFetched.map(b => b._id);
					const sequenceRes = await checkBusinessesPartOfSequence({
						campaignId: selectedSequence.id,
						businessIds
					});

					if (sequenceRes.data) {
						Object.entries(sequenceRes.data.data).forEach(
							([key, value]) => {
								if (value) {
									// @ts-ignore
									setBusinessesAlreadyInSequence(prev =>
										new Set(prev).add(key)
									);
								}
							}
						);
					}
				}

				if (allBusinessesSelect === 'SELECTED_ALL') {
					setSelectedBusinesses(
						businessesToAdd.filter(
							business =>
								!businessesToRemove.includes(business._id)
						)
					);
				}

				setHasMoreBusinessesToFetch(
					businessesCount > businessesToAdd.length
				);
				setBusinessesInLists(businessesToAdd);
			}
		} finally {
			loadingBusiness.current = false;
			setIsBusinessesLoading(false);
			if (businessesFirstLoading) setBusinessesFirstLoading(false);
		}
	}

	useEffect(async () => {
		let isMounted = true;

		async function fetchData() {
			try {
				await fetchBusinessesInLists({
					page: businessesScrollPage
				});
			} catch (error) {
				if (isMounted) {
					console.error(error);
				}
			}
		}

		await fetchData();

		return () => {
			isMounted = false; // Cleanup flag to prevent state updates
		};
	}, [businessesScrollPage]);

	return {
		fetchBusinessesLists,
		fetchBusinessesInLists,
		getListsFromSelectedBusinesses,
		getBusinessesInSequence,
		loadingBusiness: loadingBusiness.current,
		businessesFirstLoading
	};
}
